import { OFFICIAL_SOURCES } from '../officialSources';
import { mergeStepGuided } from './sharedJourneyGuided';

export const ABC_GUIDED_META = {
	productName: 'Guided Journey',
	tagline: 'Always know the next step — complete each task with confidence.',

	preparationTimeline: [
		{ daysBefore: 90, label: 'Passport validity', task: 'Check passport valid 6+ months beyond return', stepId: 'visa' },
		{ daysBefore: 75, label: 'Book international flights', task: 'Book return flights to Kathmandu', stepId: 'flights' },
		{ daysBefore: 60, label: 'Buy travel insurance', task: 'Policy with 4,500m altitude + helicopter evacuation', stepId: 'insurance' },
		{ daysBefore: 45, label: 'Fitness training', task: 'Hike hills/stairs 3× weekly. Break in boots.', stepId: 'packing' },
		{ daysBefore: 30, label: 'Buy core gear', task: 'Boots, sleeping bag, down jacket — or Kathmandu rental', stepId: 'packing' },
		{ daysBefore: 14, label: 'Apply for visa', task: 'Online form or plan VOA at airport', stepId: 'visa' },
		{ daysBefore: 7, label: 'Withdraw NPR cash', task: 'Cash for trail — teahouses are cash-only', stepId: 'kathmandu' },
		{ daysBefore: 3, label: 'Travel to Pokhara', task: 'Bus or flight KTM → Pokhara, arrange trailhead taxi', stepId: 'kathmandu' },
		{ daysBefore: 0, label: 'Trek begins', task: 'Drive to Nayapul, start walking', stepId: 'lukla' },
	],

	budget: {
		currency: 'USD',
		tiers: [
			{ id: 'budget', label: 'Budget', range: '$800–1,100', description: 'Independent teahouse trek' },
			{ id: 'standard', label: 'Standard', range: '$1,200–1,500', description: 'Guide or porter, comfortable pace' },
			{ id: 'comfort', label: 'Comfort', range: '$1,600–1,800', description: 'Private guide, Pokhara hotels' },
		],
		items: [
			{ id: 'flights-intl', label: 'International flights', budget: 700, standard: 900, comfort: 1200 },
			{ id: 'flights-pokhara', label: 'KTM ↔ Pokhara (flight or bus)', budget: 15, standard: 100, comfort: 100 },
			{ id: 'visa', label: 'Visa (30 days)', budget: 50, standard: 50, comfort: 50 },
			{ id: 'insurance', label: 'Travel insurance', budget: 50, standard: 80, comfort: 120 },
			{ id: 'permits', label: 'ACAP + TIMS', budget: 40, standard: 40, comfort: 40 },
			{ id: 'guide', label: 'Guide (8 days)', budget: 0, standard: 200, comfort: 280 },
			{ id: 'porter', label: 'Porter (8 days)', budget: 0, standard: 120, comfort: 160 },
			{ id: 'food-trail', label: 'Food on trail (8 days)', budget: 200, standard: 280, comfort: 350 },
			{ id: 'lodging-trail', label: 'Lodging on trail', budget: 80, standard: 120, comfort: 180 },
			{ id: 'kathmandu-pokhara', label: 'Kathmandu + Pokhara hotels', budget: 100, standard: 180, comfort: 300 },
			{ id: 'tips', label: 'Tips', budget: 30, standard: 60, comfort: 100 },
			{ id: 'emergency', label: 'Emergency fund', budget: 80, standard: 120, comfort: 150 },
		],
	},

	documentsWallet: [
		{ id: 'passport', label: 'Passport', note: 'Valid 6+ months beyond return' },
		{ id: 'visa', label: 'Nepal visa', note: '30-day tourist visa' },
		{ id: 'insurance', label: 'Travel insurance policy', note: '4,500m altitude cover' },
		{ id: 'flight-intl', label: 'International flight ticket', note: 'Home city ↔ Kathmandu' },
		{ id: 'permit-acap', label: 'ACAP permit', note: 'Annapurna Conservation Area' },
		{ id: 'permit-tims', label: 'TIMS card (if required)', note: 'Verify current NTB policy' },
		{ id: 'hotel-ktm', label: 'Kathmandu hotel confirmation', note: 'Thamel, first/last nights' },
		{ id: 'hotel-pokhara', label: 'Pokhara hotel confirmation', note: 'Lakeside, pre/post trek' },
		{ id: 'emergency', label: 'Emergency contacts card', note: 'Insurer, Tourist Police 1144' },
		{ id: 'photos', label: 'Passport photos (×4)', note: 'For permits and visa' },
	],

	packingChecklist: [
		{ id: 'boots', label: 'Hiking boots (broken in)', category: 'Footwear', weight: '1.2 kg', buyAtHome: 'Outdoor retailer', rentKtm: 'From $2/day', needOct: true },
		{ id: 'socks', label: 'Trekking socks (×4)', category: 'Footwear', weight: '200g', buyAtHome: 'Any', rentKtm: 'Buy locally', needOct: true },
		{ id: 'sleeping-bag', label: 'Sleeping bag (-10°C rated)', category: 'Sleep', weight: '1.2 kg', buyAtHome: 'Outdoor shop', rentKtm: '$2/day', needOct: true },
		{ id: 'down-jacket', label: 'Packable down jacket', category: 'Clothing', weight: '400g', buyAtHome: 'Outdoor brand', rentKtm: '$2/day', needOct: true },
		{ id: 'rain-shell', label: 'Waterproof rain jacket', category: 'Clothing', weight: '300g', buyAtHome: 'Any', rentKtm: '$2/day', needOct: true },
		{ id: 'fleece', label: 'Fleece mid-layer', category: 'Clothing', weight: '350g', buyAtHome: 'Any', rentKtm: 'Buy in Thamel', needOct: true },
		{ id: 'trek-pants', label: 'Trekking pants (×2)', category: 'Clothing', weight: '500g', buyAtHome: 'Any', rentKtm: 'Buy locally', needOct: true },
		{ id: 'gloves', label: 'Insulated gloves', category: 'Clothing', weight: '150g', buyAtHome: 'Outdoor shop', rentKtm: '$1/day', needOct: true },
		{ id: 'hat-warm', label: 'Warm beanie', category: 'Clothing', weight: '80g', buyAtHome: 'Any', rentKtm: 'Buy locally', needOct: true },
		{ id: 'sunglasses', label: 'Sunglasses (UV400)', category: 'Clothing', weight: '50g', buyAtHome: 'Required', rentKtm: 'Buy locally', needOct: true },
		{ id: 'poles', label: 'Trekking poles (×2)', category: 'Gear', weight: '500g', buyAtHome: 'Outdoor shop', rentKtm: '$1/day', needOct: true },
		{ id: 'daypack', label: 'Daypack (25–35L)', category: 'Gear', weight: '800g', buyAtHome: 'Carry daily', rentKtm: 'Rarely rented', needOct: true },
		{ id: 'headlamp', label: 'Headlamp + batteries', category: 'Gear', weight: '100g', buyAtHome: 'Any', rentKtm: 'Buy in Thamel', needOct: true },
		{ id: 'water', label: 'Water bottles + purification', category: 'Gear', weight: '200g', buyAtHome: 'Tablets or filter', rentKtm: 'Buy locally', needOct: true },
		{ id: 'powerbank', label: 'Power bank', category: 'Electronics', weight: '350g', buyAtHome: 'Electronics', rentKtm: 'Buy locally', needOct: true },
		{ id: 'firstaid', label: 'First aid + blister plasters', category: 'Health', weight: '200g', buyAtHome: 'Pharmacy', rentKtm: 'Pharmacy', needOct: true },
		{ id: 'sunscreen', label: 'Sunscreen SPF 50+', category: 'Health', weight: '100g', buyAtHome: 'Pharmacy', rentKtm: 'Buy locally', needOct: true },
	],

	stepGuided: mergeStepGuided({
		dream: {
			objective: 'Confirm Annapurna Base Camp is the right trek for you',
			why: 'ABC is the best first high-altitude trek — but still demands fitness and realistic altitude expectations',
			estimatedTime: '15 minutes',
			taskChecklist: [
				{ id: 'decided', label: 'I have decided to trek ABC' },
				{ id: 'fitness-honest', label: 'I honestly assessed my fitness level' },
				{ id: 'dates-rough', label: 'I have rough travel dates in mind' },
			],
		},
		research: {
			objective: 'Lock in season, duration, and budget',
			why: 'October is peak — book Pokhara hotels early. Monsoon is miserable on ABC steps',
			estimatedTime: '30 minutes',
			prerequisites: ['dream'],
			taskChecklist: [
				{ id: 'season-chosen', label: 'Best season chosen for your dates' },
				{ id: 'duration-set', label: 'Trek duration decided (7–10 days)' },
				{ id: 'budget-range', label: 'Budget range understood ($800–1,800)' },
			],
		},
		flights: {
			objective: 'Book international flights and plan KTM → Pokhara transfer',
			why: 'No Lukla flight needed — but you still need Kathmandu entry and Pokhara access',
			estimatedTime: '1 hour',
			prerequisites: ['visa'],
			taskChecklist: [
				{ id: 'intl-booked', label: 'International flights booked' },
				{ id: 'pokhara-planned', label: 'KTM → Pokhara transport planned' },
			],
		},
		insurance: {
			objective: 'Purchase insurance with 4,500m altitude and helicopter evacuation',
			why: 'Helicopter rescue from Annapurna region costs thousands without cover',
			estimatedTime: '30 minutes',
			prerequisites: ['research'],
			taskChecklist: [
				{ id: 'policy-purchased', label: 'Insurance policy purchased' },
				{ id: 'altitude-verified', label: 'Altitude limit verified (4,500m+)' },
				{ id: 'emergency-saved', label: 'Emergency number saved offline' },
			],
		},
		packing: {
			objective: 'Pack for 7–10 days at altitude',
			why: 'Limited gear above Chhomrong — pack correctly before the trail',
			estimatedTime: '2 hours',
			prerequisites: ['research'],
			usesPackingChecklist: true,
			taskChecklist: [
				{ id: 'boots-broken-in', label: 'Boots broken in (4+ weeks)' },
				{ id: 'checklist-complete', label: 'Packing checklist completed' },
			],
		},
		permits: {
			objective: 'Understand ACAP and TIMS requirements',
			why: 'Trekking without ACAP = fine at checkpoint',
			estimatedTime: '10 minutes',
			prerequisites: ['flights'],
			taskChecklist: [
				{ id: 'acap-understood', label: 'ACAP permit requirement understood' },
				{ id: 'tims-checked', label: 'TIMS requirement verified with NTB' },
			],
		},
		'permit-offices': {
			objective: 'Obtain ACAP permit before the trail',
			why: 'Checkpoints at entry to conservation area — no permit, no trek',
			estimatedTime: '45 minutes',
			prerequisites: ['permits'],
			taskChecklist: [
				{ id: 'acap-obtained', label: 'ACAP permit obtained' },
				{ id: 'tims-obtained', label: 'TIMS obtained (if required)' },
			],
		},
		lukla: {
			objective: 'Reach trailhead from Pokhara',
			why: 'Road access is ABC\'s advantage — no flight weather risk',
			estimatedTime: 'Half day',
			prerequisites: ['kathmandu'],
			taskChecklist: [
				{ id: 'pokhara-arrived', label: 'Arrived in Pokhara' },
				{ id: 'trailhead-taxi', label: 'Taxi to Nayapul arranged' },
			],
		},
		trek: {
			objective: 'Complete the trek to ABC and back',
			why: 'The glacier amphitheatre is the payoff — pace and acclimatize',
			estimatedTime: '7–10 days',
			prerequisites: ['lukla'],
			taskChecklist: [
				{ id: 'abc-reached', label: 'Reached Annapurna Base Camp' },
				{ id: 'safe-return', label: 'Returned safely to Pokhara' },
			],
		},
	}),
};
